import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { safeReturnPath } from "../../shared/origins";
import { devSignIn, SESSION_QUERY_KEY, useSession } from "../auth-client";

export const Route = createFileRoute("/sign-in")({
  validateSearch: (search: Record<string, unknown>): { next?: string } => ({
    next: typeof search.next === "string" ? search.next : undefined,
  }),
  component: SignInPage,
});

function SignInPage() {
  const { data: session, isPending } = useSession();
  // Where to land after signing in: the page the route guard bounced us from
  // (passed as `?next=`), validated to a safe same-origin path, else the
  // workspace home.
  const { next } = Route.useSearch();
  const validated = safeReturnPath(next);
  // Never send the user back to the sign-in page itself (a crafted
  // `next=/sign-in` would otherwise bounce them through it again); fall back to
  // the workspace home.
  const dest =
    validated &&
    new URL(validated, window.location.origin).pathname !== "/sign-in"
      ? validated
      : "/";
  const qc = useQueryClient();
  const [devError, setDevError] = useState<string | null>(null);
  const [devBusy, setDevBusy] = useState(false);
  // Defaults to the standard dev user so you can usually just click in; change
  // it to sign in as a different user (e.g. to test multi-user presence).
  const [devUser, setDevUser] = useState("dev");

  // Once a session exists (already signed in, or just did via dev sign-in),
  // leave for `dest`. A full-document nav — not SPA — so the target route boots
  // with the freshly-set session cookie, mirroring the Google-OAuth flow's
  // server-side redirect (and the sign-out reload in auth-client). `dest` is an
  // opaque already-resolved same-origin path, so this also avoids typed-route
  // navigation to a splat path.
  useEffect(() => {
    if (session) window.location.assign(dest);
  }, [session, dest]);

  if (isPending || session) {
    return (
      <div className="flex h-dvh items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  async function handleDevSignIn() {
    setDevError(null);
    setDevBusy(true);
    try {
      await devSignIn(devUser);
      // The cookie is now set; refetch the session so the redirect effect fires.
      await qc.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    } catch (err) {
      setDevError(err instanceof Error ? err.message : "Dev sign-in failed");
      setDevBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-6 px-6 text-center">
      <h1 className="font-serif text-3xl font-semibold tracking-tight">
        Hearth
      </h1>
      <p className="text-muted-foreground">
        Private family workspace. Sign-in is restricted to the household.
      </p>
      <a
        // Carry the return path through Google's round-trip: the worker seals it
        // into the signed OAuth `state` and redirects back here after the
        // callback. Omitted for the home destination so tenant sign-in keeps its
        // default (and sign-up on the apex still lands on `/welcome`).
        href={
          dest === "/"
            ? "/api/auth/sign-in/google"
            : `/api/auth/sign-in/google?next=${encodeURIComponent(dest)}`
        }
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
      >
        Continue with Google
      </a>
      {import.meta.env.DEV && (
        <div className="flex flex-col items-center gap-2 border-t border-border pt-4 text-sm">
          <input
            type="text"
            value={devUser}
            onChange={(e) => setDevUser(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !devBusy) void handleDevSignIn();
            }}
            placeholder="username"
            spellCheck={false}
            autoCapitalize="off"
            aria-label="Dev username"
            className="w-48 rounded-md border border-border bg-background px-3 py-1.5 text-center text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <button
            type="button"
            disabled={devBusy}
            className="rounded-md border border-border px-3 py-1.5 text-muted-foreground hover:bg-accent disabled:opacity-50"
            onClick={handleDevSignIn}
          >
            {devBusy
              ? "Signing in…"
              : `Dev sign-in as ${devUser.trim() || "dev"}`}
          </button>
          {devError && <div className="text-destructive">{devError}</div>}
        </div>
      )}
    </div>
  );
}
